// Entprellter Abruf fuer Felder mit Vorschlaegen (ST-SEA-002 S2a): erst nach
// einer Pause im Tippen abrufen, und jede neue Anfrage bricht die vorige ab.
// Der Abbruch spart Server und Netz; die Laufnummer bleibt als zweites Netz,
// weil ein `abrufen` das Signal nicht beachten muss.
//
// Die Steuerung ist ohne React gebaut, damit eine Probe sie ohne Browser und
// ohne Uhr pruefen kann (`planen` ist dort ein Tor).

import { useEffect, useRef, useState } from 'react';

export type AbrufStand<A> =
  | { readonly zustand: 'ruht' }
  // `antwort` ist die vorige, damit die Liste beim Nachladen nicht springt.
  | { readonly zustand: 'laedt'; readonly antwort?: A }
  | { readonly zustand: 'fertig'; readonly antwort: A }
  | { readonly zustand: 'fehler' };

/** Plant `ausfuehren` nach `ms` und gibt das Abbestellen zurueck. */
export type Planen = (ausfuehren: () => void, ms: number) => () => void;

export interface AbrufSteuerung {
  /** Eine neue Anfrage; `undefined` heisst: nichts abrufen. */
  readonly anfragen: (anfrage: string | undefined) => void;
  /** Bricht Wartendes und Laufendes ab, ohne einen Stand zu melden. */
  readonly beenden: () => void;
}

const fensterPlanen: Planen = (ausfuehren, ms) => {
  const zeit = window.setTimeout(ausfuehren, ms);
  return () => window.clearTimeout(zeit);
};

export function abrufSteuerung<A>(optionen: {
  readonly verzoegerungMs: number;
  readonly abrufen: (anfrage: string, signal: AbortSignal) => Promise<A>;
  readonly melden: (stand: AbrufStand<A>) => void;
  readonly planen?: Planen;
}): AbrufSteuerung {
  const planen = optionen.planen ?? fensterPlanen;
  let laufnummer = 0;
  let abbestellen: (() => void) | undefined;
  let laufend: AbortController | undefined;
  let letzte: { readonly antwort: A } | undefined;

  const beenden = (): void => {
    laufnummer += 1;
    abbestellen?.();
    abbestellen = undefined;
    laufend?.abort();
    laufend = undefined;
  };

  const anfragen = (anfrage: string | undefined): void => {
    beenden();
    if (anfrage === undefined) {
      letzte = undefined;
      optionen.melden({ zustand: 'ruht' });
      return;
    }
    const meine = laufnummer;
    optionen.melden(letzte === undefined ? { zustand: 'laedt' } : { zustand: 'laedt', antwort: letzte.antwort });
    abbestellen = planen(() => {
      abbestellen = undefined;
      const steuerung = new AbortController();
      laufend = steuerung;
      optionen.abrufen(anfrage, steuerung.signal).then(
        (antwort) => {
          if (meine === laufnummer) {
            letzte = { antwort };
            optionen.melden({ zustand: 'fertig', antwort });
          }
        },
        () => {
          // Ein abgebrochener Lauf ist nie die juengste Anfrage; er meldet nichts.
          if (meine === laufnummer) {
            optionen.melden({ zustand: 'fehler' });
          }
        },
      );
    }, optionen.verzoegerungMs);
  };

  return { anfragen, beenden };
}

/**
 * Ruft `abrufen` fuer `anfrage` entprellt ab. Eine neue `wiederholung`
 * stoesst denselben Abruf erneut an ("Erneut versuchen").
 */
export function useEntprellterAbruf<A>(optionen: {
  readonly anfrage: string | undefined;
  readonly verzoegerungMs: number;
  readonly abrufen: (anfrage: string, signal: AbortSignal) => Promise<A>;
  readonly wiederholung?: number;
}): AbrufStand<A> {
  const [stand, setStand] = useState<AbrufStand<A>>({ zustand: 'ruht' });
  // Immer das juengste `abrufen`: Anmeldung und Adresse koennen wechseln,
  // ohne dass deshalb neu abgerufen werden muss.
  const aktuell = useRef(optionen);
  aktuell.current = optionen;
  // Eine Steuerung je Feld, damit die vorige Antwort beim Nachladen bleibt.
  const [steuerung] = useState(() =>
    abrufSteuerung<A>({
      verzoegerungMs: optionen.verzoegerungMs,
      abrufen: (text, signal) => aktuell.current.abrufen(text, signal),
      melden: setStand,
    }),
  );
  const { anfrage, wiederholung } = optionen;

  useEffect(() => {
    steuerung.anfragen(anfrage);
  }, [steuerung, anfrage, wiederholung]);
  useEffect(() => steuerung.beenden, [steuerung]);

  return stand;
}
